import DoospLayout from "@/components/doosp/DoospLayout";
import { Link } from "react-router-dom";
import { FileText, Download, ExternalLink, BookOpen, Video, Shield } from "lucide-react";

const RESOURCES = [
  {
    category: "Standards & Guidelines",
    icon: Shield,
    items: [
      { title: "OSHA 29 CFR 1910.1030 — Bloodborne Pathogens", href: "https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.1030", external: true },
      { title: "CDC Guidelines for Isolation Precautions", href: "https://www.cdc.gov/infectioncontrol/guidelines/isolation/", external: true },
      { title: "WHO IPC Guideline (2022 update)", href: "https://www.who.int/publications/i/item/9789240012110", external: true },
      { title: "ANSI Z136.3 — Safe Use of Lasers in Health Care", href: "https://www.lia.org/store/product/ansi-z1363-safe-use-lasers-health-care-2018", external: true },
    ],
  },
  {
    category: "Downloadable Templates",
    icon: FileText,
    items: [
      { title: "Exposure Control Plan — template", downloadable: true },
      { title: "PPE Selection Matrix — one-page", downloadable: true },
      { title: "Post-exposure follow-up checklist", downloadable: true },
      { title: "Clinical waste segregation chart", downloadable: true },
    ],
  },
  {
    category: "Reading Room",
    icon: BookOpen,
    items: [
      { title: "The evolving landscape of needlestick injuries in ambulatory settings", href: "/news", external: false },
      { title: "Why PPE fit testing matters more than PPE selection", href: "/news", external: false },
      { title: "Interpreting your BBP exposure incident report", href: "/news", external: false },
      { title: "Case studies: infection control failures and lessons learned", href: "/news", external: false },
    ],
  },
  {
    category: "For Educators",
    icon: Video,
    items: [
      { title: "Instructor onboarding — apply here", href: "/become-instructor", external: false },
      { title: "Curriculum development guidelines", downloadable: true },
      { title: "Case study authoring toolkit", downloadable: true },
    ],
  },
];

export default function Resources() {
  return (
    <DoospLayout>
      <section className="border-b border-zinc-200 bg-gradient-to-b from-white to-zinc-50">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-4">Resource centre</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="resources-heading">
            Standards, tools, and<br />
            <span className="text-red-800">field-ready resources.</span>
          </h1>
          <p className="text-lg text-zinc-600 mt-6 max-w-3xl leading-relaxed">
            A curated library of regulatory documents, downloadable templates, and continuing
            education materials for safety professionals and educators.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 py-20 space-y-16">
          {RESOURCES.map((section) => (
            <div key={section.category}>
              <div className="flex items-center gap-3 mb-6">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-red-50">
                  <section.icon className="h-5 w-5 text-red-800" />
                </div>
                <h2 className="font-display text-2xl font-bold">{section.category}</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {section.items.map((item) => {
                  const Icon = item.downloadable ? Download : (item.external ? ExternalLink : FileText);
                  const disabled = item.downloadable;
                  return (
                    <a
                      key={item.title}
                      href={disabled ? "#" : item.href}
                      onClick={disabled ? (e) => { e.preventDefault(); } : undefined}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noreferrer" : undefined}
                      className={`flex items-center gap-3 p-4 rounded-lg border border-zinc-200 transition ${
                        disabled ? "bg-zinc-50 opacity-70 cursor-not-allowed" : "bg-white hover:border-red-600 hover:bg-red-50/40"
                      }`}
                    >
                      <Icon className={`h-4 w-4 flex-shrink-0 ${disabled ? "text-zinc-400" : "text-red-800"}`} />
                      <span className="text-sm font-medium flex-1">{item.title}</span>
                      {disabled && <span className="text-[10px] font-mono uppercase text-amber-700 bg-amber-50 px-2 py-0.5 rounded">Soon</span>}
                    </a>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-zinc-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold">Looking for a specific resource?</h2>
          <p className="text-zinc-400 mt-3">
            Our safety librarians can help you find or produce the specific reference you need.
          </p>
          <Link to="/contact" className="mt-6 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white h-11 px-6 rounded-md text-sm font-medium">
            Contact the resource team
          </Link>
        </div>
      </section>
    </DoospLayout>
  );
}
